import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Activity, TrendingUp, Users, Package, LifeBuoy, ShoppingCart, ArrowUpRight, AlertTriangle, LogIn } from 'lucide-react';


// ── Dashboard mockup ──────────────────────────────────────────────────────────

function CommerceDashboard() {
  const bars = [35, 55, 42, 70, 52, 85, 63, 92, 71, 100, 80, 95];
  return (
    <div className="relative w-full max-w-sm mx-auto select-none">
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 shadow-xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Revenu mensuel</p>
            <p className="text-white text-3xl font-bold mt-0.5">€128 400</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <div className="flex items-center gap-1 bg-emerald-400/25 text-emerald-300 text-xs font-semibold px-2 py-0.5 rounded-full">
                <ArrowUpRight size={11} />+24 %
              </div>
              <span className="text-white/40 text-xs">vs mois précédent</span>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <TrendingUp size={18} className="text-white" />
          </div>
        </div>
        <div className="flex items-end gap-1 h-16">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 rounded-t" style={{
              height: `${h}%`,
              background: i >= bars.length - 3 ? 'rgba(255,255,255,0.80)'
                : i >= bars.length - 6 ? 'rgba(255,255,255,0.45)'
                : 'rgba(255,255,255,0.25)',
            }} />
          ))}
        </div>
        <div className="flex justify-between mt-2">
          {['Jan', 'Mar', 'Mai', 'Jul', 'Sep', 'Nov', 'Déc'].map(m => (
            <span key={m} className="text-white/35 text-[9px] font-medium">{m}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        {[
          { value: '342', label: 'Clients actifs', icon: Users },
          { value: '89 %', label: 'Taux conv.',    icon: ShoppingCart },
          { value: '24',   label: 'Deals actifs',  icon: TrendingUp },
        ].map(({ value, label, icon: Icon }) => (
          <div key={label} className="bg-white/10 border border-white/15 rounded-xl p-3 text-center">
            <Icon size={14} className="text-white/50 mx-auto mb-1" />
            <p className="text-white text-base font-bold leading-none">{value}</p>
            <p className="text-white/50 text-[10px] mt-0.5 leading-tight">{label}</p>
          </div>
        ))}
      </div>
      <div className="absolute -top-3 -right-4 bg-emerald-400 text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
        <ShoppingCart size={11} />Nouveau deal — €42 K
      </div>
    </div>
  );
}

// ── Login page ────────────────────────────────────────────────────────────────

export default function LoginPage() {
  // Le retour de redirection (`code` + `state`) est traité par AuthContext, qui
  // monte avant cette page : rien à faire ici que de lancer le flux.
  const { login, error: authError } = useAuth();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    setError(null);
    setRedirecting(true);
    try {
      await login();
    } catch (err) {
      // signinRedirect() échoue avant tout appel réseau si crypto.subtle est
      // indisponible — c'est le cas hors contexte sécurisé (http://192.168.x).
      setError(
        window.isSecureContext
          ? `Impossible de joindre Keycloak : ${err.message}`
          : 'Contexte non sécurisé : la connexion exige HTTPS ou http://localhost.'
      );
      setRedirecting(false);
    }
  };

  const displayedError = error || authError;
  const loading = redirecting;

  return (
    <div className="min-h-screen bg-background flex">

      {/* ── Left panel — branding ── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 50%, #3b82f6 100%)' }}>

        {/* Decorative blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-blue-400/20 translate-x-1/3 translate-y-1/3" />
          <div className="absolute top-1/3 -right-10 w-52 h-52 rounded-full bg-white/5" />
          <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.5" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shadow-inner">
            <Activity size={22} className="text-white" />
          </div>
          <span className="text-white text-xl font-extrabold tracking-tight">SalePulse</span>
        </div>

        {/* Headline + illustration */}
        <div className="relative space-y-8">
          <div className="space-y-3">
            <p className="text-blue-200 text-sm font-semibold uppercase tracking-widest">Plateforme commerciale B2B</p>
            <h1 className="text-4xl font-extrabold text-white leading-tight">
              Boostez vos ventes.<br />
              Suivez chaque deal<br />
              en temps réel.
            </h1>
            <p className="text-white/70 text-base leading-relaxed max-w-sm">
              Pipeline, contacts, devis, factures et catalogue produits — tout ce dont votre équipe commerciale a besoin, dans une seule interface.
            </p>
          </div>
          <CommerceDashboard />
          <div className="flex flex-wrap gap-2.5">
            {[
              { icon: TrendingUp, label: 'Pipeline commercial' },
              { icon: Users,      label: 'CRM contacts' },
              { icon: Package,    label: 'Catalogue produits' },
              { icon: LifeBuoy,   label: 'Support tickets' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-white text-xs font-medium">
                <Icon size={13} />{label}
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <p className="text-white/40 text-xs">© 2026 SalePulse — Gestion commerciale nouvelle génération</p>
        </div>
      </div>

      {/* ── Right panel — login ── */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm space-y-8" data-testid="login-card">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
              <Activity size={19} className="text-white" />
            </div>
            <span className="text-foreground text-lg font-extrabold tracking-tight">SalePulse</span>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">Bienvenue</h2>
            <p className="text-muted-foreground mt-1.5 text-sm">
              Connectez-vous à votre espace SalePulse avec votre compte ELIKA.
            </p>
          </div>

          {displayedError && (
            <div className="p-3.5 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2" data-testid="login-error">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{displayedError}</span>
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full h-12 flex items-center justify-center gap-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition-colors disabled:opacity-60 shadow-sm"
            data-testid="sso-login-btn"
          >
            {loading
              ? <Loader2 size={18} className="animate-spin" />
              : <LogIn size={18} className="flex-shrink-0" />}
            {loading ? 'Redirection…' : 'Se connecter'}
          </button>

          <p className="text-xs text-center text-muted-foreground">
            Authentification unique ELIKA — vous serez redirigé vers{' '}
            <span className="font-medium text-foreground">auth.elikateam.com</span>,
            puis vers votre compte Microsoft.
            <br />
            L'accès et les droits sont attribués par votre administrateur.
          </p>
        </div>
      </div>
    </div>
  );
}
